// Chuyển từ VUONGDATWOOD_V2/src/features/cong-luong/payroll-sheet.ts
// Mô hình chung của bảng lương khi đưa ra ngoài (file Excel và bản in ký nhận), để hai đường ra luôn
// cùng thứ tự cột, cùng tô màu, cùng con số tổng. Cột đi theo khai báo của mẫu, không viết chết.
import { PAYROLL_CATALOG_VARIABLES } from './catalog';
import { canonicalPayrollCode } from './domain';

// Sắc thái cột theo bảng lương Excel công ty đang ký nhận. Khai theo mã biến vì tên biến sửa được.
const SHEET_ACCENTS = {
    PCTN: 'alert',
    PCK: 'alert',
    UNG: 'alert',
    GK: 'alert',
    TNCN: 'alert',
    BH_NLD: 'highlight',
    TC: 'total',
    TCHI: 'net',
};

// Đơn giá, tỷ lệ, mức hằng số là con số của một người, cộng dọc cột ra số vô nghĩa.
// Ngoài danh sách này còn loại theo tiền tố: DG* là đơn giá, TL_* là tỷ lệ.
const NON_ADDITIVE_CODES = new Set(['MLT', 'NCC', 'GIO_CC', 'HSL_TC', 'GTBT', 'GTNPT_1', 'MIEN_PCC', 'SO_NPT']);

export function isAdditiveColumn(code, dataType) {
    if (dataType === 'TY_LE') return false;
    if (NON_ADDITIVE_CODES.has(code)) return false;
    return !code.startsWith('DG') && !code.startsWith('TL_');
}

const CATALOG_BY_CODE = new Map(PAYROLL_CATALOG_VARIABLES.map((item) => [item.code, item]));
const CATALOG_ORDER = new Map(PAYROLL_CATALOG_VARIABLES.map((item, index) => [item.code, index]));

const UNASSIGNED_TEAM = 'Chưa phân tổ';
const UNASSIGNED_TEMPLATE = 'Chưa gán mẫu';

function columnWidth(header, dataType) {
    const byHeader = Math.ceil(header.length * 0.85) + 2;
    return Math.min(22, Math.max(dataType === 'TIEN' ? 14 : 11, byHeader));
}

function toColumn(code, name, dataType) {
    const header = name.trim() || code;
    return {
        code,
        header,
        dataType,
        accent: SHEET_ACCENTS[code] ?? 'none',
        width: columnWidth(header, dataType),
        additive: isAdditiveColumn(code, dataType),
        decimals: 0,
    };
}

const MAX_DECIMALS = 2;

function hasFraction(value) {
    return Math.abs(value - Math.round(value)) > 1e-9;
}

// Số chữ số lẻ của cả cột suy từ số liệu trong cột: có người nghỉ nửa buổi thì cả cột hiện 2 số lẻ
// cho thẳng hàng, toàn số nguyên thì không kéo theo số 0 vô nghĩa. Tiền luôn chẵn đồng.
function resolveDecimals(column, values) {
    if (column.dataType === 'TIEN') return 0;
    return values.some((value) => value !== null && hasFraction(value)) ? MAX_DECIMALS : 0;
}

// Nhóm chưa gán mẫu: lấy hợp mọi mã biến họ có, xếp theo danh mục. Thừa cột còn hơn sót người.
function fallbackColumns(employees) {
    const codes = new Set();
    for (const employee of employees) {
        for (const code of Object.keys(employee.values)) codes.add(canonicalPayrollCode(code));
    }
    return Array.from(codes)
        .sort((left, right) => (CATALOG_ORDER.get(left) ?? Number.MAX_SAFE_INTEGER) - (CATALOG_ORDER.get(right) ?? Number.MAX_SAFE_INTEGER) || left.localeCompare(right))
        .map((code) => toColumn(code, CATALOG_BY_CODE.get(code)?.name ?? code, CATALOG_BY_CODE.get(code)?.dataType ?? 'TIEN'));
}

function templateColumns(template) {
    const seen = new Set();
    return template.columns
        .filter((column) => column.visible !== false)
        .slice()
        .sort((left, right) => (left.order ?? 0) - (right.order ?? 0))
        .flatMap((column) => {
            const code = canonicalPayrollCode(column.code);
            if (seen.has(code)) return [];
            seen.add(code);
            const catalog = CATALOG_BY_CODE.get(code);
            return [toColumn(code, column.name || catalog?.name || code, column.dataType || catalog?.dataType || 'TIEN')];
        });
}

// Các dòng liên tiếp cùng tổ, để gộp ô cột TỔ như bảng lương giấy.
function buildTeamSpans(rows) {
    const spans = [];
    rows.forEach((row, index) => {
        const last = spans[spans.length - 1];
        if (last && last.to === row.to) last.length += 1;
        else spans.push({ to: row.to, start: index, length: 1 });
    });
    return spans;
}

/**
 * Dựng bảng lương theo từng mẫu; mỗi mẫu một bảng vì cột của lương sản phẩm và lương ngày công không
 * xếp chung một hàng tiêu đề được.
 * input.templates: [{ code, name, columns: [{ code, name, dataType, order, visible }] }]
 * input.employees: [{ soHd, hoTen, to, templateCode, templateName, values }]
 */
export function buildPayrollSheetModel(input) {
    const templateByCode = new Map(input.templates.map((template) => [template.code, template]));
    const groups = new Map();
    for (const employee of input.employees) {
        const key = employee.templateCode ?? '';
        groups.set(key, [...(groups.get(key) ?? []), employee]);
    }

    const sheets = Array.from(groups, ([code, employees]) => {
        const template = templateByCode.get(code);
        const declaredColumns = template ? templateColumns(template) : fallbackColumns(employees);
        const rows = employees
            .map((employee) => ({
                stt: 0,
                soHd: employee.soHd,
                hoTen: employee.hoTen,
                to: employee.to?.trim() || UNASSIGNED_TEAM,
                values: Object.fromEntries(declaredColumns.map((column) => {
                    const raw = employee.values[column.code];
                    return [column.code, typeof raw === 'number' && Number.isFinite(raw) ? raw : null];
                })),
            }))
            .sort((left, right) => left.to.localeCompare(right.to, 'vi') || left.hoTen.localeCompare(right.hoTen, 'vi'))
            .map((row, index) => ({ ...row, stt: index + 1 }));
        const columns = declaredColumns.map((column) => ({
            ...column,
            decimals: resolveDecimals(column, rows.map((row) => row.values[column.code])),
        }));
        const totals = Object.fromEntries(columns.map((column) => [
            column.code,
            column.additive ? rows.reduce((sum, row) => sum + (row.values[column.code] ?? 0), 0) : null,
        ]));
        return {
            templateCode: code,
            templateName: template?.name || employees.find((employee) => employee.templateName)?.templateName || UNASSIGNED_TEMPLATE,
            columns,
            rows,
            teamSpans: buildTeamSpans(rows),
            totals,
        };
    // Nhóm chưa gán mẫu luôn xuống cuối: đó là phần việc còn dở, không phải một mẫu lương thật.
    }).sort((left, right) => Number(Boolean(!left.templateCode)) - Number(Boolean(!right.templateCode)) || left.templateName.localeCompare(right.templateName, 'vi'));

    return {
        title: input.title?.trim() || `BẢNG THANH TOÁN LƯƠNG THÁNG ${input.period}`,
        period: input.period,
        sheets,
    };
}

// Ô bằng 0 để trống: bảng lương giấy chỉ điền ô có tiền, một rừng số 0 khó dò đúng dòng của mình.
export function formatPayrollSheetValue(value, decimals = 0) {
    if (value === null || value === undefined || value === 0) return '';
    return new Intl.NumberFormat('vi-VN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);
}
